// @ts-nocheck
// based on: https://github.com/malaman/js-image-zoom, converted to typescript and modified extensively.
import colors from "@store/Themes/common/styleguide/config/colors";

export interface ZoomOptions {
    width?: number;
    height?: number;
    zoomWidth?: number;
    fillContainer?: boolean;
    scale?: number;
    offset?: {
        vertical?: string;
        horizontal?: string;
    };
    zoomContainer?: HTMLElement;
    zoomStyle?: string;
    zoomLensStyle?: string;
    windowHeight?: number;
    windowWidth?: number;
    index?: number;
}

interface SourceImg {
    element: HTMLImageElement | null;
    width: number;
    height: number;
}

interface ZoomedImgOffset {
    vertical: string;
    horizontal: string;
}

interface ZoomedImg {
    element: HTMLElement | null;
    width: number;
    height: number;
}

interface ZoomLens {
    element: HTMLElement | null;
    width: number;
    height: number;
}
interface ZoomContainer {
    element: HTMLElement | null;
    width: number;
    height: number;
}
interface Data {
    sourceImg: SourceImg;
    zoomedImgOffset: ZoomedImgOffset;
    zoomedImg: ZoomedImg;
    zoomLens: ZoomLens;
    zoomPosition?: string;
    zoomContainer?: HTMLElement;
    zoomedContainer: ZoomContainer;
}

interface StartingPosition {
    x: number;
    y: number;
}

function ImageZoom(container: HTMLElement, opts: ZoomOptions, startingPosition?: StartingPosition) {
    const options: ZoomOptions = opts || {};
    if (!container) {
        return;
    }
    const data: Data = {
        zoomedContainer: {
            element: null,
            width: 0,
            height: 0,
        },
        sourceImg: {
            element: null,
            width: 0,
            height: 0,
        },
        zoomedImgOffset: {
            vertical: options.offset?.vertical || "0px",
            horizontal: options.offset?.horizontal || "0px",
        },
        zoomedImg: {
            element: null,
            width: 0,
            height: 0,
        },
        zoomLens: {
            element: null,
            width: 0,
            height: 0,
        },
        zoomContainer: options.zoomContainer || container,
    };

    const div: HTMLElement = document.createElement("div");
    const lensDiv: HTMLElement = document.createElement("div");

    let offset: { left: number; top: number };
    const scale = options?.scale ?? 1;
    let scaleX = 1;
    let scaleY = 1;
    let height = options?.height ?? 0;
    let width = options?.height ?? 0;
    function getOffset(el: HTMLElement | null): { left: number; top: number } {
        if (el) {
            const elRect = el.getBoundingClientRect();

            return { left: elRect.left, top: elRect.top };
        }
        return { left: 0, top: 0 };
    }

    function leftLimit(min: number): number {
        return width - min;
    }

    function topLimit(min: number): number {
        return height - min;
    }

    function getValue(val: number, min: number, max: number): number {
        if (val < min) {
            return min;
        }
        if (val > max) {
            return max;
        }
        return val;
    }

    function getPosition(v: number, min: number, max: number): number {
        const value = getValue(v, min, max);
        return value - min;
    }

    function zoomLensLeft(left: number): number {
        const leftMin = data.zoomLens.width / 2;
        return getPosition(left, leftMin, leftLimit(leftMin));
    }

    function zoomLensTop(top: number): number {
        const topMin = data.zoomLens.height / 2;
        return getPosition(top, topMin, topLimit(topMin));
    }

    function setZoomedImgSize(): void {
        if (!data.sourceImg.element) {
            return;
        }
        if (!data.zoomedImg.element) {
            return;
        }
        const { windowHeight, windowWidth } = options;
        const { vertical, horizontal } = data.zoomedImgOffset;
        const zoomedContainerParentWidth = data.zoomContainer!.parentElement!.clientWidth;
        const zoomedContainerParentHeight = data.zoomContainer!.parentElement!.clientHeight;
        const zoomContainerWidth = windowWidth || zoomedContainerParentWidth;
        const zoomContainerHeight = windowHeight || zoomedContainerParentHeight;
        const zoomedHeight = height * scale;
        const zoomedWidth = width * scale;
        data.zoomedContainer.width = zoomContainerWidth;
        data.zoomedContainer.height = zoomContainerHeight;

        data.zoomedImg.height = height;
        data.zoomedImg.width = zoomedWidth;
        data.zoomedImg.element.style.width = `${zoomedWidth}px`;
        data.zoomedImg.element.style.height = `${zoomedHeight}px`;
        data.zoomedImg.element.style.backgroundSize = `contain`;
        data.zoomContainer!.style.width = `${zoomContainerWidth}px`;
        data.zoomContainer!.style.height = `${zoomContainerHeight}px`;
        data.zoomContainer!.style.top = vertical;
        data.zoomContainer!.style.left = horizontal;
        data.zoomContainer!.style.zIndex = "1000";
    }

    function onSourceImgLoad(): void {
        if (!data.sourceImg.element || !data.zoomedImg.element || !data.zoomContainer) {
            return;
        }
        data.zoomContainer.style.display = "block";
        height = height || data.sourceImg.element.height;
        width = width || data.sourceImg.element.width;
        data.sourceImg.element.style.height = options.fillContainer ? "100%" : `${height}px`;
        data.sourceImg.element.style.width = options.fillContainer ? "100%" : `${width}px`;

        setZoomedImgSize();

        if (data.zoomedImg.element) {
            if (options.zoomStyle) {
                data.zoomedImg.element.style.cssText += options.zoomStyle;
            }
        }
        if (options.zoomLensStyle && data.zoomLens.element) {
            data.zoomLens.element.style.cssText += options.zoomLensStyle;
        } else if (data.zoomLens.element) {
            data.zoomLens.element.style.background = "white";
            data.zoomLens.element.style.border = `1px solid ${colors?.common?.backgroundColorWarmGrey}`;
            data.zoomLens.element.style.opacity = "0.4";
        }

        offset = getOffset(data.sourceImg.element);

        scaleX = data.zoomedContainer.width / data.sourceImg.element.width;
        scaleY = data.zoomedContainer.height / data.sourceImg.element.height;

        data.zoomLens.width = (width / scale) * scaleX;
        data.zoomLens.height = (height / scale) * scaleY;

        if (data.zoomLens.element) {
            data.zoomLens.element.style.position = "absolute";
            data.zoomLens.element.style.width = `${data.zoomLens.width}px`;
            data.zoomLens.element.style.height = `${data.zoomLens.height}px`;
            data.zoomLens.element.style.pointerEvents = "none";
        }
        data.zoomedImg.element!.style.display = "block";
        if (startingPosition) {
            events.handleMouseMove({
                clientX: startingPosition.x,
                clientY: startingPosition.y,
            } as MouseEvent);
        }
    }

    function setup(): void {
        data.sourceImg.element = container.children[0] as HTMLImageElement;

        if (data.sourceImg.element?.nodeName !== "IMG") {
            const images = container.querySelectorAll("img");
            if (options.index !== undefined) {
                data.sourceImg.element = images[options.index];
            } else {
                data.sourceImg.element = images[0];
            }
            container = data.sourceImg.element.parentElement!;
        }

        container.style.position = "relative";
        data.sourceImg.element.style.width = options.fillContainer ? "100%" : width ? `${width}px` : "auto";
        data.sourceImg.element.style.height = options.fillContainer ? "100%" : height ? `${height}px` : "auto";
        data.sourceImg.element.style.objectFit = "contain";
        data.zoomLens.element = container.appendChild(lensDiv);
        data.zoomLens.element.classList.add("js-image-zoom__zoomed-area");
        data.zoomedImg.element = data.zoomContainer!.appendChild(div);
        data.zoomedImg.element.classList.add("js-image-zoom__zoomed-image");
        data.zoomedImg.element.style.backgroundImage = `url('${data.sourceImg.element.src}')`;
        data.zoomedImg.element.style.backgroundRepeat = "no-repeat";
        // data.zoomedImg.element.style.display = "none";

        data.zoomedImg.element.style.position = "relative";
        data.zoomedImg.element.style.top = "0px";
        data.zoomedImg.element.style.left = "0px";

        container.addEventListener("mousemove", events.handleMouseMove);
        container.addEventListener("mouseenter", events.handleMouseEnter);
        container.addEventListener("mouseleave", events.handleMouseLeave);
        data.zoomLens.element.addEventListener("mouseenter", events.handleMouseEnter);
        data.zoomLens.element.addEventListener("mouseleave", events.handleMouseLeave);
        window.addEventListener("scroll", events.handleScroll);
    }

    function kill(): void {
        if (!data.zoomLens.element || !data.zoomedImg.element || !data.sourceImg.element) {
            return;
        }
        container.removeEventListener("mousemove", events.handleMouseMove);
        container.removeEventListener("mouseenter", events.handleMouseEnter);
        container.removeEventListener("mouseleave", events.handleMouseLeave);
        data.zoomLens.element.removeEventListener("mouseenter", events.handleMouseEnter);
        data.zoomLens.element.removeEventListener("mouseleave", events.handleMouseLeave);
        window.removeEventListener("scroll", events.handleScroll);

        if (data.zoomLens.element && data.zoomedImg.element && container.hasChildNodes()) {
            if (container.contains(data.zoomLens.element)) {
                container.removeChild(data.zoomLens.element);
            }
            if (data.zoomContainer!.contains(data.zoomedImg.element)) {
                data.zoomContainer!.removeChild(data.zoomedImg.element);
                data.zoomContainer!.style.display = "none";
            }
        }

        data.sourceImg.element.style.width = "";
        data.sourceImg.element.style.height = "";
    }

    const events = {
        handleMouseMove(event: MouseEvent): void {
            offset = getOffset(data.sourceImg.element);
            if (offset && data.zoomLens.element && data.zoomedImg.element) {
                const offsetX = zoomLensLeft(event.clientX - offset.left);
                const offsetY = zoomLensTop(event.clientY - offset.top);
                const backgroundTop = offsetX * scale;
                const backgroundRight = offsetY * scale;
                const backgroundPosition = `-${backgroundTop}px -${backgroundRight}px`;
                data.zoomedImg.element.style.backgroundPosition = backgroundPosition;
                data.zoomLens.element.style.transform = `translate(${offsetX}px, ${
                    offsetY + data.sourceImg.element?.offsetTop!
                }px)`;
                data.zoomLens.element.style.display = "block";
                data.zoomLens.element.style.left = "0px";
                data.zoomLens.element.style.top = "0px";
            }
        },
        handleMouseEnter(): void {
            if (data.zoomedImg.element) {
                data.zoomedImg.element.style.display = "block";
                if (data.zoomContainer) {
                    data.zoomContainer.style.display = "block";
                }
            }
            if (data.zoomLens.element) {
                data.zoomLens.element.style.display = "block";
            }
        },
        handleMouseLeave(): void {
            if (!data.zoomedImg.element || !data.zoomLens.element) {
                return;
            }
            data.zoomedImg.element.style.display = "none";
            data.zoomLens.element.style.display = "none";
            if (data.zoomContainer) {
                data.zoomContainer.style.display = "none";
            }
        },
        handleScroll(): void {
            offset = getOffset(data.sourceImg?.element || container);
        },
    };

    setup();

    if (data.sourceImg.element?.complete) {
        onSourceImgLoad();
    } else if (data.sourceImg.element) {
        data.sourceImg.element.onload = onSourceImgLoad;
    }

    return {
        setup,
        kill,
        _getInstanceInfo() {
            return {
                setup,
                kill,
                onSourceImgLoad,
                data,
                options,
            };
        },
    };
}

export default ImageZoom;
